import matchUnchangedBlocks from "@ext/git/core/Diff/logic/matchUnchangedBlocks";
import type { DiffChange } from "@gaurussel/tiptap-diff-utility";
import type { Node } from "prosemirror-model";

export interface MovedMarker {
	pairId: string;
	side: "from" | "to";
	pos: number;
	direction: "up" | "down";
	preview: string;
}

const PREVIEW_WORDS = 6;

const previewOf = (node: Node | undefined): string => {
	const text = node?.textContent?.trim() ?? "";
	const words = text.split(/\s+/).filter(Boolean).slice(0, PREVIEW_WORDS);
	return words.length ? `${words.join(" ")}${text.split(/\s+/).length > PREVIEW_WORDS ? "…" : ""}` : "";
};

/** `posA` points into the old document, so the "from" position is inferred from the nearest matched block after it. */
const movedMarkers = (changes: DiffChange[], before: Node, after: Node): MovedMarker[] => {
	const moved = changes.filter(
		(change) => change.type === "moved" && change.posA !== undefined && change.posB !== undefined,
	);
	if (!moved.length) return [];

	const pairs = matchUnchangedBlocks(changes, before, after);
	const markers: MovedMarker[] = [];

	moved.forEach((change) => {
		const posA = change.posA as number;
		const posB = change.posB as number;

		const anchor = pairs.filter((pair) => pair.posA > posA).sort((left, right) => left.posA - right.posA)[0];

		const fromPos = anchor ? anchor.posB : pairs.length ? after.content.size : undefined;

		const pairId = `moved-${posA}-${posB}`;
		const direction: "up" | "down" = fromPos !== undefined && posB > fromPos ? "down" : "up";
		const preview = previewOf(change.nodeB ?? change.nodeA);

		if (fromPos !== undefined) {
			markers.push({ pairId, side: "from", pos: fromPos, direction, preview });
		}
		const toPos = direction === "up" && change.nodeB ? posB + change.nodeB.nodeSize : posB;
		markers.push({ pairId, side: "to", pos: toPos, direction, preview });
	});

	return markers;
};

export default movedMarkers;
